import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import { uploadFile } from '@/lib/upload';
import { mockJobs, type Job } from '@/mocks/jobs';
import { mapDbJob, formatSalary } from '@/lib/jobs';
import { useAuth } from '@/context/AuthContext';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { copied, copy } = useCopyToClipboard();

  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [cvUrl, setCvUrl] = useState('');
  const [letterUrl, setLetterUrl] = useState('');
  const [uploadingCv, setUploadingCv] = useState(false);
  const [uploadingLetter, setUploadingLetter] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState(false);
  const cvInputRef = useRef<HTMLInputElement>(null);
  const letterInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (err) throw err;
      if (data) {
        setJob(mapDbJob(data as never));
      } else {
        setJob(mockJobs.find((j) => j.id === id) || null);
      }
    } catch {
      setJob(mockJobs.find((j) => j.id === id) || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (user && showForm) {
      if (profile?.full_name) setFullName(profile.full_name);
      if (profile?.email || user.email) setEmail(profile?.email || user.email || '');
      if (profile?.phone) setPhone(profile.phone);
    }
  }, [user, profile, showForm]);

  const handleApplyClick = () => {
    if (!user) {
      navigate('/auth/login', { state: { from: { pathname: `/jobs/${id}` } } });
      return;
    }
    setShowForm(true);
    setSuccess(false);
  };

  const handleCvUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !user) return;
    setUploadingCv(true);
    setFormError('');
    try {
      const url = await uploadFile(files[0], `jobs/${user.id}/cv`);
      setCvUrl(url);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Échec de l'envoi du CV.");
    } finally {
      setUploadingCv(false);
      if (cvInputRef.current) cvInputRef.current.value = '';
    }
  };

  const handleLetterUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !user) return;
    setUploadingLetter(true);
    setFormError('');
    try {
      const url = await uploadFile(files[0], `jobs/${user.id}/letters`);
      setLetterUrl(url);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Échec de l'envoi de la lettre.");
    } finally {
      setUploadingLetter(false);
      if (letterInputRef.current) letterInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !job) return;
    if (!fullName.trim() || !email.trim()) {
      setFormError('Votre nom et votre email sont obligatoires.');
      return;
    }
    setSubmitting(true);
    setFormError('');

    const { error: err } = await supabase.from('job_applications').insert({
      job_id: job.id,
      applicant_id: user.id,
      full_name: fullName.trim(),
      email: email.trim(),
      phone: phone.trim() || null,
      message: message.trim() || null,
      cv_url: cvUrl || null,
      cover_letter_url: letterUrl || null,
    });

    setSubmitting(false);
    if (err) {
      setFormError(err.message);
      return;
    }
    setSuccess(true);
    setShowForm(false);
    setMessage('');
    setCvUrl('');
    setLetterUrl('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-500/10">
            <i className="ri-loader-4-line text-2xl text-secondary-400 animate-spin" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-secondary-500/10">
              <i className="ri-error-warning-line text-3xl text-secondary-400" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-foreground-50 mb-2">
              Offre introuvable
            </h1>
            <p className="text-sm text-foreground-400 mb-6">
              Cette offre d&apos;emploi n&apos;existe plus ou a été retirée.
            </p>
            <Link
              to="/jobs"
              className="inline-block px-6 py-2.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors text-sm"
            >
              Voir toutes les offres
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-24 md:pt-32 pb-16">
          {/* Header */}
          <div className="mb-6">
            <nav aria-label="Breadcrumb" className="text-xs text-foreground-400 mb-4 flex items-center gap-2 flex-wrap">
              <Link to="/jobs" className="hover:text-secondary-400 transition-colors">Emplois</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-500">{job.category}</span>
            </nav>

            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
              <div className="flex gap-4">
                <div className="w-16 h-16 rounded-xl bg-secondary-500/10 flex items-center justify-center shrink-0">
                  <i className="ri-briefcase-4-line text-3xl text-secondary-400" />
                </div>
                <div>
                  <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 leading-tight">
                    {job.title}
                  </h1>
                  <p className="text-foreground-400 mt-1">{job.company}</p>
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary-500/10 text-secondary-400 whitespace-nowrap">
                      {job.category}
                    </span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-background-200/70 text-foreground-500 whitespace-nowrap">
                      {job.contractType}
                    </span>
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-map-pin-line" />
                      {job.city}
                    </span>
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-time-line" />
                      {job.postedAt}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-left md:text-right shrink-0 flex flex-col gap-3">
                <p className="font-heading font-bold text-xl text-secondary-400 whitespace-nowrap">
                  {formatSalary(job)}
                </p>
                <button
                  type="button"
                  onClick={() => copy(window.location.href)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-background-300/60 text-foreground-50 rounded-lg text-sm font-medium hover:bg-background-200/60 transition-colors whitespace-nowrap"
                  aria-label={`Partager ${job.title}`}
                  title="Copier le lien"
                >
                  <i className={copied ? 'ri-check-line text-secondary-400' : 'ri-share-line'} />
                  {copied ? 'Lien copié !' : 'Partager'}
                </button>
              </div>
            </div>
          </div>

          {/* Success banner */}
          {success && (
            <div className="p-4 rounded-xl bg-accent-500/10 border border-accent-400/30 text-sm text-accent-400 mb-6 flex items-center gap-3">
              <i className="ri-checkbox-circle-line text-xl" />
              Votre candidature a bien été envoyée à l&apos;entreprise. Vous serez contacté(e) par email ou téléphone.
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-6">{error}</div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-6">
              <section className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <h2 className="font-heading font-semibold text-lg text-foreground-50 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-500/10">
                    <i className="ri-file-text-line text-secondary-400" />
                  </span>
                  Description du poste
                </h2>
                <p className="text-sm text-foreground-400 leading-relaxed whitespace-pre-line">
                  {job.description}
                </p>
              </section>

              <section className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <h2 className="font-heading font-semibold text-lg text-foreground-50 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-500/10">
                    <i className="ri-check-double-line text-secondary-400" />
                  </span>
                  Profil recherché
                </h2>
                <p className="text-sm text-foreground-400 leading-relaxed whitespace-pre-line">
                  {job.requirements}
                </p>
              </section>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 lg:sticky lg:top-24">
                <h3 className="font-heading font-semibold text-foreground-50 mb-4">Postuler à cette offre</h3>
                <button
                  onClick={handleApplyClick}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors text-sm whitespace-nowrap mb-3"
                >
                  <i className="ri-send-plane-line" />
                  {user ? 'Postuler maintenant' : 'Se connecter pour postuler'}
                </button>
                {job.contactPhone && (
                  <a
                    href={`tel:${job.contactPhone.replace(/\s/g, '')}`}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 border border-background-300/60 text-foreground-50 font-semibold rounded-lg hover:bg-background-200/60 transition-colors text-sm whitespace-nowrap mb-3"
                  >
                    <i className="ri-phone-line" />
                    Contacter l&apos;entreprise
                  </a>
                )}
                {job.contactEmail && (
                  <a
                    href={`mailto:${job.contactEmail}`}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 border border-background-300/60 text-foreground-50 font-semibold rounded-lg hover:bg-background-200/60 transition-colors text-sm whitespace-nowrap"
                  >
                    <i className="ri-mail-line" />
                    Envoyer un email
                  </a>
                )}
              </div>

              {/* Application form */}
              {showForm && user && (
                <div className="p-6 rounded-xl bg-background-100 border border-secondary-400/30">
                  <h3 className="font-heading font-semibold text-foreground-50 mb-4">Votre candidature</h3>
                  {formError && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-4">
                      {formError}
                    </div>
                  )}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground-400 mb-1.5">Nom complet *</label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-secondary-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-400 mb-1.5">Email *</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-secondary-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-400 mb-1.5">Téléphone</label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+228 90 00 00 00"
                        className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-secondary-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-400 mb-1.5">Message de motivation</label>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        maxLength={1000}
                        placeholder="Présentez-vous brièvement et expliquez votre motivation..."
                        className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-secondary-400 resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground-400 mb-1.5">CV (PDF)</label>
                      <button
                        type="button"
                        onClick={() => cvInputRef.current?.click()}
                        disabled={uploadingCv}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border border-background-300/60 text-foreground-50 rounded-md hover:bg-background-200/60 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        <i className="ri-upload-2-line" />
                        {uploadingCv ? 'Envoi...' : cvUrl ? 'CV envoyé ✓' : 'Joindre mon CV'}
                      </button>
                      <input ref={cvInputRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => handleCvUpload(e.target.files)} />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground-400 mb-1.5">Lettre de motivation (PDF)</label>
                      <button
                        type="button"
                        onClick={() => letterInputRef.current?.click()}
                        disabled={uploadingLetter}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border border-background-300/60 text-foreground-50 rounded-md hover:bg-background-200/60 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        <i className="ri-upload-2-line" />
                        {uploadingLetter ? 'Envoi...' : letterUrl ? 'Lettre envoyée ✓' : 'Joindre ma lettre'}
                      </button>
                      <input ref={letterInputRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => handleLetterUpload(e.target.files)} />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting || uploadingCv || uploadingLetter}
                      className="w-full px-5 py-3 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors disabled:opacity-60 text-sm whitespace-nowrap"
                    >
                      {submitting ? 'Envoi en cours...' : 'Envoyer ma candidature'}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default JobDetail;